/**
 * Toast notification abstraction.
 *
 * Provides a single `displayMessage(type, body, options)` method that renders a
 * transient toast. Internally it delegates to BootstrapVue's `$bvToast`, but
 * every consumer talks to THIS method instead of `$bvToast` directly.
 *
 * Why this exists (Vue 3 forward-compatibility):
 *   The same `displayMessage` helper was copy-pasted into ~30 components, each
 *   calling `this.$bvToast.toast(...)` directly. `$bvToast` is removed in
 *   bootstrap-vue-next (the Vue 3 successor), so every one of those copies would
 *   be an independent migration site. Funnelling them through one mixin means
 *   the eventual swap to bootstrap-vue-next's toast controller touches ONE file.
 *   Mixins also map cleanly onto Vue 3 composables, so this can later become a
 *   `useToast()` composable without changing call sites' intent.
 *
 * `displayMessage(messageType, messageBody)` is the canonical title-only
 * notification (message in the title, hidden body, 5s auto-hide) — byte-for-byte
 * identical to the previous inline helper.
 *
 * `showToast` is the same renderer with an options object for the few call sites
 * that legitimately differ from the canonical shape (a longer auto-hide, a
 * de-dupe `id`, a visible/custom body, or any raw b-toast option). It is NOT a
 * new abstraction — it is the parameterized core of this one, so a component
 * that needs a non-default toast can keep a single local `displayMessage`
 * override delegating to `showToast` instead of calling `$bvToast` directly.
 *
 * @param {string} messageType  BootstrapVue variant, e.g. "success" | "warning".
 * @param {string} messageBody  Text shown as the toast title.
 * @param {object} [options]    { body?, autoHideDelay?, id?, ...rawBToastProps }.
 *                              `body` overrides the (hidden) toast body;
 *                              anything else is forwarded to b-toast and can
 *                              override the title-only defaults (e.g. bodyClass).
 */
export const ToastMixin = {
  methods: {
    showToast(messageType, messageBody, options = {}) {
      const { body, autoHideDelay, id, ...toastOptions } = options;
      this.$bvToast.toast(body !== undefined ? body : "Empty Body", {
        title: messageBody,
        variant: messageType,
        headerClass: "py-2 px-4",
        bodyClass: "d-none",
        autoHideDelay: autoHideDelay !== undefined ? autoHideDelay : 5000,
        solid: true,
        ...(id !== undefined ? { id } : {}),
        ...toastOptions
      });
    },
    displayMessage(messageType, messageBody, options = {}) {
      this.showToast(messageType, messageBody, options);
    }
  }
};

export default ToastMixin;
